import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { AppContext } from "../../Context";
import MUITextFieldSelect from "../../Components/MUITextFieldSelect";
import {
  SUB_VENUE_REGISTRATION,
  SUBVENUE_TITLE_DESC,
  ROUTE_REGISTRATION_SUCCESS,
} from "../../Util/VMConstants";
import { align_center, VM_Colors, registrationStepShellSx } from "../../Styles/VMStyles";
import TitleDesc from "../../Components/Custom/TitleDesc";
import { toast } from "react-toastify";
import { API_SUB_VENUE_INFO, API_SUB_VENUE_REGISTRATION } from "../../Util/URLConstants";
import { postApi } from "../../Util/ApiService";
import { useNavigate } from "react-router-dom";
import { isEmpty, toTitleCaseInput } from "../../Util/UtilHelper";
import { VM_sub_venueTypes } from "../../Helper/RegistrationHelper";
import ErrorIcon from "@mui/icons-material/Error";
import Loader from "../../Components/Loader";
import StyledInputField from "../../Components/Custom/StyledInputField";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import { useEffect } from "react";
import axios from "axios";

const SubVenueRegistration = () => {
  const navigate = useNavigate();

  const [navItems, setNavItems] = useState([
    {
      sub_venue_name: "",
      sub_venue_type: "Select",
      sub_venue_custom_type: "",
      sub_venue_capacity: "",
      sub_venue_status: "Active",
    },
  ]);

  const [loading, setLoading] = useState(false);

  const { formValues, handleBack } = useContext(AppContext);

  // ⭐ added: detect unsaved data
  const hasUnsavedData = () => {
    return navItems.some((item) =>
      item.sub_venue_name ||
      (item.sub_venue_type && item.sub_venue_type !== "Select") ||
      item.sub_venue_custom_type ||
      item.sub_venue_capacity ||
      (item.sub_venue_status && item.sub_venue_status !== "Active")
    );
  };

  // ⭐ added: custom back handler with confirmation
  const handleBackWithConfirm = () => {
    if (hasUnsavedData()) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. If you go back, the entered data will be lost.\nDo you want to continue?"
      );
      if (!confirmLeave) return;
    }
    handleBack();
  };

  const handleNavItemChange = (index, field, value) => {
    const updated = [...navItems];
    updated[index][field] = value;
    setNavItems(updated);
  };

  const areAllFormsFilled = () => {
    return navItems.every(
      (item) =>
        !isEmpty(item.sub_venue_name) &&
        !isEmpty(item.sub_venue_type) &&
        item.sub_venue_type !== "Select" &&
        (item.sub_venue_type !== "Others" ||
          !isEmpty(item.sub_venue_custom_type)) &&
        !isEmpty(item.sub_venue_capacity) &&
        !isEmpty(item.sub_venue_status)
    );
  };

  const addNavItem = () => {
    if (!areAllFormsFilled()) {
      toast.error(
        "Please fill in all existing SubVenue details before adding a new one."
      );
      return;
    }
    setNavItems([
      ...navItems,
      {
        sub_venue_name: "",
        sub_venue_type: "Select",
        sub_venue_custom_type: "",
        sub_venue_capacity: "",
        sub_venue_status: "Active",
      },
    ]);
  };

  const removeField = (index) => {
    if (navItems.length === 1) return;
    setNavItems(navItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!areAllFormsFilled()) {
      toast.error(
        "Please fill in all existing SubVenue details before submitting."
      );
      return;
    }

    if (isEmpty(formValues.user_id) || isEmpty(formValues.venue_id)) {
      toast.error("User is not logged in. Please try again.");
      return;
    }

    setLoading(true);

    const cleanedNavItems = navItems.map((item) => ({
      sub_venue_name: item.sub_venue_name,
      sub_venue_type:
        item.sub_venue_type === "Others"
          ? item.sub_venue_custom_type
          : item.sub_venue_type,
      sub_venue_capacity: item.sub_venue_capacity,
      sub_venue_status: item.sub_venue_status,
    }));

    const obj = {
      user_id: formValues.user_id,
      venue_id: formValues.venue_id,
      sub_venues: cleanedNavItems,
    };

    try {
      const resp = await postApi(API_SUB_VENUE_REGISTRATION, obj);
      setLoading(false);
      if (resp.status_code === 200) {
        navigate(
          `/${ROUTE_REGISTRATION_SUCCESS}`,
          { state: { user_id: formValues.user_id } },
          { replace: true }
        );
      } else {
        toast.error("Sub venue registration failed. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error:", error);
      toast.error("Something went wrong.");
    }
  };


  
  useEffect(() => {
  async function fetchSubVenueInfo() {
    if (isEmpty(formValues.user_id)) return;

    try {
      setLoading(true);

      const resp = await axios.get(
        `${API_SUB_VENUE_INFO}?user_id=${formValues.user_id}`
      );

      const data = resp?.data;

      if (
        data?.status_code === 200 &&
        Array.isArray(data.sub_venue_info) &&
        data.sub_venue_info.length > 0
      ) {
        // ✅ Map API data to UI structure
        const mapped = data.sub_venue_info.map((item) => ({
          sub_venue_name: item.sub_venue_name || "",
          sub_venue_type:
            VM_sub_venueTypes.some(v => v.value === item.sub_venue_type)
              ? item.sub_venue_type
              : "Others",
          sub_venue_custom_type:
            VM_sub_venueTypes.some(v => v.value === item.sub_venue_type)
              ? ""
              : item.sub_venue_type,
          sub_venue_capacity: item.sub_venue_capacity || "",
          sub_venue_status: item.sub_venue_status || "Active",
        }));

        setNavItems(mapped);
      }
      // else → keep default single empty form
    } catch (error) {
      console.error("SubVenue fetch error:", error);
    } finally {
      setLoading(false);
    }
  }

  fetchSubVenueInfo();
}, [formValues.user_id]);


  return (
    <Box sx={{ width: "100%", px: { xs: 1, md: 2 }, ...registrationStepShellSx }}>
      <TitleDesc
        title={SUB_VENUE_REGISTRATION}
        subtitle={SUBVENUE_TITLE_DESC}
      />

      <Grid container spacing={3} mt={2}>
        {navItems.map((item, index) => (
          <Grid item xs={12} key={index}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StyledInputField
                      heading="Sub Venue Name"
                      placeholder="Enter Sub Venue Name"
                      value={item.sub_venue_name}
                      onChange={(e) =>
                        handleNavItemChange(
                          index,
                          "sub_venue_name",
                          toTitleCaseInput(e.target.value)
                        )
                      }
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Stack spacing={0.5}>
                      <MuiTypographyMandatory
                        label="Sub Venue Type"
                        required
                      />
                      <MUITextFieldSelect
                        value={item.sub_venue_type}
                        onChange={(e) =>
                          handleNavItemChange(
                            index,
                            "sub_venue_type",
                            e.target.value
                          )
                        }
                        options={VM_sub_venueTypes}
                        fullWidth
                      />
                    </Stack>
                  </Grid>

                  {item.sub_venue_type === "Others" && (
                    <Grid item xs={12} sm={6} md={3}>
                      <StyledInputField
                        heading="Custom Sub Venue Type"
                        placeholder="Enter custom type"
                        value={item.sub_venue_custom_type}
                        onChange={(e) =>
                          handleNavItemChange(
                            index,
                            "sub_venue_custom_type",
                            e.target.value
                          )
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={2}>
                    <StyledInputField
                      heading="Capacity"
                      placeholder="Enter Capacity"
                      value={item.sub_venue_capacity}
                      onChange={(e) =>
                        handleNavItemChange(
                          index,
                          "sub_venue_capacity",
                          e.target.value
                        )
                      }
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Stack spacing={0.5}>
                      <MuiTypographyMandatory
                        label="Sub Venue Status"
                        required
                      />
                      <MUITextFieldSelect
                        value={item.sub_venue_status}
                        onChange={(e) =>
                          handleNavItemChange(
                            index,
                            "sub_venue_status",
                            e.target.value
                          )
                        }
                        options={[
                          { label: "Active (Default)", value: "Active" },
                          { label: "Inactive", value: "Inactive" },
                        ]}
                        fullWidth
                      />
                      {item.sub_venue_status === "Inactive" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                            color: "warning.main",
                          }}
                        >
                          <ErrorIcon fontSize="small" />
                          <Typography variant="body2">
                            This Subvenue will not be available for booking.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={12} md={1} sx={align_center}>
                    <Box sx={{ height: "100%", ...align_center, mt: "15px" }}>
                      <IconButton
                        onClick={() => removeField(index)}
                        disabled={navItems.length === 1}
                        aria-label="delete sub venue"
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add New SubVenue Button */}
      <Box
        sx={{
          mt: 5,    
          mb: 5,
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addNavItem}
          sx={{
            px: 3,
            borderRadius: 2,
          }}
        >
          Add Another SubVenue
        </Button>
      </Box>

      {/* Footer Buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          backgroundColor: VM_Colors.white,
          borderTop: "1px solid #e0e0e0",
          zIndex: 1200,
        }}
      >
        <Button
          variant="outlined"
          sx={{ minWidth: 120, mr: 3 }}

          // ⭐ replaced original handleBack
          onClick={handleBackWithConfirm}
        >
          Back
        </Button>

        <Button
          variant="contained"
          color="primary"
          sx={{
            fontWeight: "bold",
            px: 4,
            minWidth: 140,
          }}
          onClick={handleSubmit}
        >
          Next
        </Button>
      </Box>

      {loading && <Loader loading />}
    </Box>
  );
};

export default SubVenueRegistration;
